'use client'

import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react'
import { getHostGuestKeyNavLinks, getHostLoggedInKeyNavLinks } from '../assets/routes'
import { useHostSiteLinkBase } from '../HostSiteLinkBase'
import grrenLogoImage from '../assets/green-logo.png'
import Image from 'next/image'
import { useTranslation } from 'app/components/providers/TranslationProvider'
import { useRightNavData } from 'app/components/host-site/assets/utilityFunction'
import { DuoDoorOpen } from 'components/icons/duotone'
import { handleLogout } from 'utils/logout'
import { extractLangFromPath } from 'app/lib/extractLangFromPath'
import LanguageSwitcher from 'components/layout/Language/LanguageSwitcher'
import { IconCustomGoldPremium } from 'app/components/icons'
export default function Navbar() {
    const params = useParams()
    const pathname = usePathname()

    // Extract current language from params or pathname
    const paramsLang = params.lang
    const routeLang = Array.isArray(paramsLang) ? paramsLang[0] : paramsLang
    const langFromPath = extractLangFromPath(pathname || '', 'en')
    const currentLang = routeLang || langFromPath || 'en'

    const { t } = useTranslation()
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false)
    const linkBase = useHostSiteLinkBase()
    const navLinks = useMemo(() => getHostGuestKeyNavLinks(linkBase), [linkBase])
    const { user, hasAdminRole } = useRightNavData()
    const loggedInLinks = useMemo(() => {
        let links = getHostLoggedInKeyNavLinks(linkBase)
        if (!hasAdminRole) {
            links = links.filter(link => link.key !== 'admin')
        }
        if (user?.premium) {
            links = links.filter(link => link.key !== 'premium')
        }
        return links
    }, [linkBase, hasAdminRole, user?.premium])
    const handleSignOut = async () => {
        // Use the current language to ensure we stay on the same domain
        await handleLogout({ lang: currentLang })
        setIsMobileMenuOpen(false)
    }
    useEffect(() => {
        setIsMobileMenuOpen(false)
    }, [pathname])

    useEffect(() => {
        if (typeof window === 'undefined') return

        const mediaQuery = window.matchMedia('(min-width: 1024px)')

        const syncBodyScroll = () => {
            document.body.style.overflow = isMobileMenuOpen && !mediaQuery.matches ? 'hidden' : ''
        }

        syncBodyScroll()

        const handleViewportChange = () => {
            if (mediaQuery.matches) {
                setIsMobileMenuOpen(false)
            }
            syncBodyScroll()
        }

        mediaQuery.addEventListener('change', handleViewportChange)

        return () => {
            mediaQuery.removeEventListener('change', handleViewportChange)
            document.body.style.overflow = ''
        }
    }, [isMobileMenuOpen])

    const linkIsActive = (href: string) => pathname === href || (href !== linkBase && pathname.startsWith(`${href}/`))

    const activeStyles = '!text-[#b4ff00] underline decoration-[#b4ff00] underline-offset-8'
    const inactiveStyles = 'text-white transition-colors'

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className='fixed top-0 z-50 w-full border-b border-zinc-800/50 bg-[#0a0a0a]/80 px-6 py-4 backdrop-blur-md md:px-12 lg:px-24'
        >
            <div className='mx-auto flex max-w-7xl items-center justify-between'>
                <Link href={linkBase} className='text-2xl font-black tracking-tighter text-[#b4ff00]'>
                    <Image src={grrenLogoImage} alt='Disporty' className='object-contain w-auto h-[50px]' />
                </Link>

                {/* Desktop Links */}
                <div className='hidden items-center gap-8 text-sm font-medium text-zinc-300 lg:flex'>
                    {navLinks.map(link => (
                        <Link
                            key={link.key}
                            href={link.href}
                            className={linkIsActive(link.href) ? activeStyles : inactiveStyles}
                        >
                            {t?.(link.key)}
                        </Link>
                    ))}
                </div>

                {/* Actions & Language Switcher Wrapper */}
                <div className='hidden lg:flex items-center gap-4'>
                    {/* Desktop Actions */}
                    {!user ? (
                        <div className='hidden items-center gap-4 lg:flex'>
                            <Link
                                href='/login'
                                onClick={() => setIsMobileMenuOpen(false)}
                                className='hover:text-white transition-colors whitespace-nowrap'
                            >
                                {t?.('login')}
                            </Link>
                            <Link
                                href='/signup'
                                className='rounded-sm bg-[#b4ff00] px-6 py-3 text-sm font-bold !text-black transition-transform hover:scale-105 whitespace-nowrap text-center'
                            >
                                {t?.('signup')}
                            </Link>
                        </div>
                    ) : (
                        <div className='hidden items-center gap-4 lg:flex relative'>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className='flex items-center justify-center w-10 h-10 rounded-full bg-[#b4ff00] text-black font-bold focus:outline-none overflow-hidden hover:scale-105 transition-transform'
                            >
                                {user?.photoURL || user?.logo ? (
                                    <img src={user?.photoURL || user?.logo} alt={user.username || 'User'} className='w-full h-full object-cover' />
                                ) : (
                                    (user?.username || user?.email || 'U').charAt(0).toUpperCase()
                                )}
                            </button>
                            
                            <AnimatePresence>
                                {isDropdownOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        className='absolute top-full right-0 mt-4 w-64 rounded-md border border-zinc-800 bg-[#121212] shadow-lg py-2 z-50'
                                    >
                                        <div className='flex items-center gap-3 px-4 py-3 border-b border-zinc-800'>
                                            <div className='flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[#b4ff00] text-black font-bold overflow-hidden'>
                                               {user?.photoURL || user?.logo ? (
                                    <img src={user?.photoURL || user?.logo} alt={user.username || 'User'} className='w-full h-full object-cover' />
                                ) : (
                                    (user?.username || user?.email || 'U').charAt(0).toUpperCase()
                                )}
                                            </div>
                                            <div className='flex flex-col overflow-hidden'>
                                                <div className='flex items-center gap-1'>
                                                    <p className='text-sm font-medium text-white truncate'>
                                                        {user.first_name} {user.last_name}
                                                    </p>
                                                    {user?.premium && <IconCustomGoldPremium className='size-5 flex-shrink-0' />}
                                                </div>
                                                <p className='text-xs text-zinc-400 truncate'>
                                                    {user.username}
                                                </p>
                                            </div>
                                        </div>
                                        <div className='py-2 flex flex-col'>
                                            {loggedInLinks.map(link => (
                                                <Link
                                                    key={link.key}
                                                    href={link.href}
                                                    onClick={() => setIsDropdownOpen(false)}
                                                    className='px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors flex items-center gap-3'
                                                >
                                                    <span className='flex w-8 items-center justify-center'>{link.icon}</span>
                                                    {t?.(link.key)}
                                                </Link>
                                            ))}
                                        </div>
                                        <div className='border-t border-zinc-800 p-4'>
                                            <button
                                                onClick={handleSignOut}
                                                className='flex w-full items-center justify-center gap-2 rounded-sm bg-[#b4ff00] px-6 py-3 text-sm font-bold !text-black transition-transform hover:scale-105 whitespace-nowrap'
                                            >
                                                <DuoDoorOpen className='inline w-6 h-6 aspect-square' />
                                                {t?.('logout')}
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                    <LanguageSwitcher triggerClassName='w-auto border-foreground/40' contentClassName='w-auto min-w-[120px]' />
                </div>


                {/* Mobile Menu Toggle */}
                <button className='text-white lg:hidden' onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.3 }}
                        className='fixed inset-0 top-[83px] h-[calc(100vh-83px)] z-40 overflow-y-auto border-t border-zinc-800/50 bg-[#0a0a0a] px-6 pb-24 pt-6 md:px-12 lg:hidden'
                    >
                        <div className='flex flex-col gap-4 text-sm font-medium text-zinc-300'>
                            {navLinks.map(link => (
                                <Link
                                    key={link.key}
                                    href={link.href}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`${linkIsActive(link.href) ? activeStyles : inactiveStyles} text-lg`}
                                >
                                    {t?.(link.key)}
                                </Link>
                            ))}
                              <LanguageSwitcher
                                                            triggerClassName={`border-zinc-500 backdrop-blur-md h-10`}
                                                            contentClassName={`bg-transparent backdrop-blur-md`}
                                                            hoverBackgroundClassName='hover:!bg-[rgba(70,118,216)]/[.24]'
                                                            selectedBackgroundClassName='bg-[rgba(70,118,216)]/[.16] focus:bg-[rgba(70,118,216)]/[.16] hover:bg-[rgba(70,118,216)]/[.16]'
                                                        />
                            <hr className='border-zinc-800 my-2' />
                            
                            {!user ? (
                                <>
                                    <Link
                                        href='/login'
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className='block w-full text-center text-lg hover:text-white transition-colors whitespace-nowrap mt-2'
                                    >
                                        {t?.('login')}
                                    </Link>
                                    <Link
                                        href='/signup'
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className='block w-full text-center rounded-sm bg-[#b4ff00] px-6 py-3 text-sm font-bold !text-black transition-transform hover:scale-105 whitespace-nowrap mt-2'
                                    >
                                        {t?.('signup')}
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <button 
                                        onClick={() => setIsMobileUserMenuOpen(!isMobileUserMenuOpen)}
                                        className='flex items-center justify-between w-full py-2 mb-2 border-b border-zinc-800/50 pb-4 focus:outline-none text-left'
                                    >
                                        <div className='flex items-center gap-4'>
                                            <div className='flex flex-shrink-0 items-center justify-center w-12 h-12 rounded-full bg-[#b4ff00] text-black font-bold overflow-hidden'>
                                               
                                                 {user?.photoURL || user?.logo ? (
                                    <img src={user?.photoURL || user?.logo} alt={user.username || 'User'} className='w-full h-full object-cover' />
                                ) : (
                                    (user?.username || user?.email || 'U').charAt(0).toUpperCase()
                                )}
                                            </div>
                                            <div className='flex flex-col overflow-hidden'>
                                                <div className='flex items-center gap-1'>
                                                    <span className='text-base font-bold text-white truncate'>
                                                        {user.first_name} {user.last_name}
                                                    </span>
                                                    {user?.premium && <IconCustomGoldPremium className='size-5 flex-shrink-0' />}
                                                </div>
                                                <span className='text-sm text-muted-foreground truncate'>
                                                    {user.username}
                                                </span>
                                            </div>
                                        </div>
                                        <div className='text-zinc-500'>
                                            {isMobileUserMenuOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                        </div>
                                    </button>
                                    <AnimatePresence>
                                        {isMobileUserMenuOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className='flex flex-col overflow-hidden gap-4'
                                            >
                                                {loggedInLinks.map(link => (
                                                    <Link
                                                        key={link.key}
                                                        href={link.href}
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                        className={`${linkIsActive(link.href) ? activeStyles : inactiveStyles} text-lg flex items-center gap-3`}
                                                    >
                                                        <span className='flex w-8 items-center justify-center'>{link.icon}</span>
                                                        {t?.(link.key)}
                                                    </Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <button
                                        onClick={handleSignOut}
                                        className='flex w-full items-center justify-center gap-2 rounded-sm bg-[#b4ff00] px-6 py-3 text-sm font-bold !text-black transition-transform hover:scale-105 whitespace-nowrap mt-4'
                                    >
                                        <DuoDoorOpen className='inline w-6 h-6 aspect-square' />
                                        {t?.('logout')}
                                    </button>
                                </>
                            )}
                           
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    )
}
